import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import authRecoveryService from '../../../services/authRecoveryService';

const AuditLogPanel = ({ refreshTrigger }) => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    repairType: '',
    performedBy: '',
    dateFrom: '',
    dateTo: ''
  });

  useEffect(() => {
    loadAuditLogs();
  }, [refreshTrigger]);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await authRecoveryService?.getSystemRepairAuditLog(filters);
      setAuditLogs(data);
    } catch (err) {
      setError(err?.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    loadAuditLogs();
  };

  const handleClearFilters = () => {
    setFilters({
      repairType: '',
      performedBy: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  const repairTypes = [
    { value: '', label: 'All Types' },
    { value: 'auth_state_clear', label: 'Auth State Clear' },
    { value: 'captcha_reset', label: 'CAPTCHA Reset' },
    { value: 'config_fix', label: 'Config Fix' },
    { value: 'user_creation_retry', label: 'User Creation Retry' }
  ];

  if (loading && auditLogs?.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" className="w-6 h-6 text-emerald-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
          System Repair Audit Log
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Complete audit trail of all repair activities with administrator attribution
        </p>
      </div>

      {/* Filters */}
      <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Repair Type
            </label>
            <select
              value={filters?.repairType}
              onChange={(e) => handleFilterChange('repairType', e?.target?.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
            >
              {repairTypes?.map(type => (
                <option key={type?.value} value={type?.value}>
                  {type?.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Performed By
            </label>
            <input
              type="text"
              value={filters?.performedBy}
              onChange={(e) => handleFilterChange('performedBy', e?.target?.value)}
              placeholder="Email address"
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Date From
            </label>
            <input
              type="date"
              value={filters?.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Date To
            </label>
            <input
              type="date"
              value={filters?.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleApplyFilters}
            className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            Apply Filters
          </button>
          <button
            onClick={handleClearFilters}
            className="px-4 py-2 text-sm bg-gray-200 dark:bg-slate-600 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-slate-500 transition-colors"
          >
            Clear
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Audit Log Table */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-slate-700 border-b border-gray-200 dark:border-slate-600">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Repair Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Action</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Performed By</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Duration</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
              {auditLogs?.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-sm text-gray-600 dark:text-gray-400">
                    <Icon name="FileText" className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>No audit logs found</p>
                  </td>
                </tr>
              ) : (
                auditLogs?.map((log) => (
                  <tr key={log?.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-900 dark:text-white">
                        {new Date(log?.createdAt)?.toLocaleString()}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                        {log?.repairType?.replace('_', ' ')?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-900 dark:text-white">{log?.actionTaken}</p>
                      {log?.targetEntityType && (
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {log?.targetEntityType}: {log?.targetEntityId}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-900 dark:text-white">{log?.performedBy}</p>
                    </td>
                    <td className="px-4 py-3">
                      {log?.success ? (
                        <span className="px-2 py-1 text-xs font-bold rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                          SUCCESS
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-bold rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
                          FAILED
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {log?.executionTimeMs ? `${log?.executionTimeMs}ms` : 'N/A'}
                      </p>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AuditLogPanel;